import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Mail, KeyRound, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/context/language-context';
import { useAuth } from '@/context/auth';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  DialogTrigger
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

const emailLoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean().optional(),
});

const seedPhraseLoginSchema = z.object({
  seedPhrase: z.string().min(6, "Seedphrase must contain at least 6 words"),
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type EmailLoginFormValues = z.infer<typeof emailLoginSchema>;
type SeedPhraseLoginFormValues = z.infer<typeof seedPhraseLoginSchema>;
type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const LoginForm = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { login, loginWithSeedPhrase, user, isLoading: authLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("email");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // If the user is already logged in, redirect to the dashboard
  useEffect(() => {
    console.log('LoginForm: user state changed:', user ? `User ${user.id} logged in` : 'No user');
    if (user) {
      console.log('LoginForm: Redirecting logged in user to dashboard');
      // Get the path from the location state or default to dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      
      // Extract the language code from the current URL
      const pathParts = location.pathname.split('/');
      const langCode = pathParts.length > 1 ? pathParts[1] : 'en';
      
      // Make sure the redirection includes the language code
      const redirectPath = from.startsWith('/') 
        ? `/${langCode}${from.startsWith(`/${langCode}`) ? from.substring(3) : from}`
        : `/${langCode}/${from}`;
      
      console.log('LoginForm: Redirecting to:', redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate, location]);

  const emailForm = useForm<EmailLoginFormValues>({
    resolver: zodResolver(emailLoginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const seedPhraseForm = useForm<SeedPhraseLoginFormValues>({
    resolver: zodResolver(seedPhraseLoginSchema),
    defaultValues: {
      seedPhrase: '',
    },
  });

  const forgotPasswordForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  // Réinitialiser le message d'erreur lors du changement d'onglet
  useEffect(() => {
    setErrorMessage(null);
  }, [activeTab]);

  const onEmailSubmit = async (data: EmailLoginFormValues) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      console.log('Login attempt with email:', data.email);
      const result = await login(data.email, data.password);
      
      if (!result.success) {
        throw result.error || new Error("Login failed");
      }
      
      toast({
        title: "Login successful",
        description: "Welcome back!",
      });
      
      // The redirect will be handled by the useEffect when user state changes
    } catch (error) {
      console.error('Login error:', error);
      let message = "Please check your credentials and try again.";
      
      // Handle specific errors
      if (error instanceof Error && error.message) {
        if (error.message.includes("Email not confirmed")) {
          message = "Please confirm your email address before logging in. Check your inbox for a confirmation email.";
        } else if (error.message.includes("Invalid login credentials")) {
          message = "Invalid email or password. Please try again.";
        } else {
          message = error.message;
        }
      }
      
      setErrorMessage(message);
      
      toast({
        variant: "destructive",
        title: "Login failed",
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSeedPhraseSubmit = async (data: SeedPhraseLoginFormValues) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      console.log('Login with seedphrase:', data.seedPhrase);
      const result = await loginWithSeedPhrase(data.seedPhrase);
      
      if (!result.success) {
        throw result.error || new Error("Login failed");
      }
      
      toast({
        title: "Login successful",
        description: "Welcome back!",
      });
      
      // The redirect will be handled by the useEffect
    } catch (error) {
      console.error('Login error:', error);
      let message = "Please check your seedphrase and try again.";
      
      // Caster 'error' en 'Error' ou type spécifique
      if (error instanceof Error && error.message) {
        message = error.message;
      }
      
      setErrorMessage(message);
      
      toast({
        variant: "destructive",
        title: "Login failed",
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true);
    try {
      console.log('Forgot password request for:', data.email);
      // Dans une application réelle, vous appelleriez un point de terminaison d'API pour envoyer un email de réinitialisation
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simuler un appel d'API
      
      toast({
        title: "Password reset email sent",
        description: `We've sent a password reset link to ${data.email}`,
      });
      forgotPasswordForm.reset();
    } catch (error) {
      console.error('Forgot password error:', error);
      toast({
        variant: "destructive",
        title: "Request failed",
        description: "Unable to process your request. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loading = isLoading || authLoading;

  return (
    <div className="w-full max-w-md p-6 bg-card border border-border rounded-lg shadow-md">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold mb-2">Login</h1>
        <p className="text-muted-foreground">Sign in to your account</p>
      </div>
      
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      
      <Tabs 
        defaultValue="email" 
        className="w-full"
        onValueChange={(value) => setActiveTab(value)}
      >
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="email">Email & Password</TabsTrigger>
          <TabsTrigger value="seedphrase">Anonymous (Seedphrase)</TabsTrigger>
        </TabsList>
        
        <TabsContent value="email" className="space-y-4">
          <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Your email address"
                  className="pl-10"
                  {...emailForm.register('email')}
                />
              </div>
              {emailForm.formState.errors.email && (
                <p className="text-sm text-destructive">{emailForm.formState.errors.email.message}</p>
              )}
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <Label htmlFor="password">Password</Label>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="link" className="px-0 text-sm text-primary hover:underline" type="button">
                      Forgot password?
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Reset your password</DialogTitle>
                      <DialogDescription>
                        Enter your email address and we'll send you a link to reset your password.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)} className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email">Email</Label>
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="Your email address"
                          {...forgotPasswordForm.register('email')}
                        />
                        {forgotPasswordForm.formState.errors.email && (
                          <p className="text-sm text-destructive">{forgotPasswordForm.formState.errors.email.message}</p>
                        )}
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" type="button">Cancel</Button>
                        </DialogClose>
                        <Button type="submit" disabled={isLoading}>
                          {isLoading ? 'Sending...' : 'Send reset link'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Your password"
                  className="pl-10 pr-10"
                  {...emailForm.register('password')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
              {emailForm.formState.errors.password && (
                <p className="text-sm text-destructive">{emailForm.formState.errors.password.message}</p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="remember-me" 
                checked={emailForm.watch('rememberMe')}
                onCheckedChange={(checked) => {
                  emailForm.setValue('rememberMe', checked === true);
                }}
              />
              <Label 
                htmlFor="remember-me" 
                className="text-sm font-normal cursor-pointer"
                onClick={() => emailForm.setValue('rememberMe', !emailForm.watch('rememberMe'))}
              >
                Remember me
              </Label>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-pink-gradient hover:opacity-90"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Sign in'}
            </Button>
          </form>
        </TabsContent>
        
        <TabsContent value="seedphrase" className="space-y-4">
          <form onSubmit={seedPhraseForm.handleSubmit(onSeedPhraseSubmit)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="seedphrase">Seedphrase</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-5 text-muted-foreground h-4 w-4" />
                <Textarea
                  id="seedphrase"
                  placeholder="Enter at least 6 words separated by spaces"
                  className="min-h-24 pl-10"
                  {...seedPhraseForm.register('seedPhrase')}
                />
              </div>
              {seedPhraseForm.formState.errors.seedPhrase && (
                <p className="text-sm text-destructive">{seedPhraseForm.formState.errors.seedPhrase.message}</p>
              )}
            </div>
            
            <Button
              type="submit"
              className="w-full bg-pink-gradient hover:opacity-90"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Login Anonymously'}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary hover:underline font-medium">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
